import { useRef } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { CitySilhouette } from '@/components/city/CitySilhouette';
import { getCityState } from '@/utils/cityVisuals';

interface WelcomeProps {
  userName?: string;
  onStart: () => void;
}

export function Welcome({ userName, onStart }: WelcomeProps) {
  // Show city in "waking" state for welcome screen
  const cityState = getCityState(60, 100);
  const infoRef = useRef<HTMLDivElement>(null);

  const scrollToInfo = () => {
    infoRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div
      className="min-h-screen flex flex-col relative overflow-y-auto overflow-x-hidden"
      style={{ background: 'linear-gradient(180deg, #080A14 0%, #0D0F1A 30%, #1A1535 70%, #251845 100%)' }}
    >
      {/* Stars background */}
      <div className="stars-container">
        <div className="star star-1" />
        <div className="star star-2" />
        <div className="star star-3" />
        <div className="star star-4" />
        <div className="star star-5" />
        <div className="star star-6" />
        <div className="star star-7" />
        <div className="star star-8" />
        <div className="star star-9" />
        <div className="star star-10" />
        <div className="star star-11" />
        <div className="star star-12" />
      </div>

      {/* ===== First Screen: Hero ===== */}
      <div className="min-h-screen flex flex-col p-6 safe-area-top">
        <div className="flex-1 flex flex-col items-center justify-center text-center relative z-10">
          {/* Moon */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: 'easeOut' }}
            className="mb-8"
          >
            <div className="relative">
              <motion.div
                animate={{
                  boxShadow: [
                    '0 0 60px rgba(245, 230, 204, 0.2), 0 0 120px rgba(245, 230, 204, 0.1)',
                    '0 0 80px rgba(245, 230, 204, 0.3), 0 0 160px rgba(245, 230, 204, 0.15)',
                    '0 0 60px rgba(245, 230, 204, 0.2), 0 0 120px rgba(245, 230, 204, 0.1)',
                  ],
                }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                className="moon-sphere w-28 h-28"
              >
                <span className="text-4xl">☽</span>
              </motion.div>
            </div>
          </motion.div>

          {/* Fairy-tale text */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="max-w-sm mb-8"
          >
            <p className="font-serif text-xl text-accent-moon leading-relaxed italic mb-6">
              Где-то есть город, который снится тебе каждую ночь.
            </p>
            <p className="font-serif text-lg text-primary-300 leading-relaxed italic mb-6">
              Ты никогда в нём не был.
            </p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.5 }}
              className="font-serif text-lg text-accent-warm leading-relaxed italic"
            >
              Но он знает тебя.
            </motion.p>
          </motion.div>

          {/* Greeting */}
          {userName && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 2 }}
              className="text-primary-400 text-sm mb-4"
            >
              Добро пожаловать, {userName}
            </motion.p>
          )}
        </div>

        {/* City silhouette */}
        <CitySilhouette cityState={cityState} />

        {/* Scroll down CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 2.5 }}
          className="relative z-20 py-4"
        >
          <button
            onClick={scrollToInfo}
            className="w-full py-3 flex flex-col items-center gap-1 text-primary-300 hover:text-accent-moon transition-colors"
          >
            <span className="text-base font-serif">Узнать больше</span>
            <motion.span
              animate={{ y: [0, 6, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
              className="text-lg"
            >
              ↓
            </motion.span>
          </button>
        </motion.div>
      </div>

      {/* ===== Second Screen: Info ===== */}
      <div ref={infoRef} className="min-h-screen flex flex-col p-6 safe-area-bottom relative z-10">
        <div className="flex-1 flex flex-col justify-center space-y-6 max-w-sm mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="font-serif text-xl text-accent-moon mb-4">Что тебя ждёт</h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="p-4 rounded-xl bg-primary-800/20 border border-primary-700/30"
          >
            <span className="text-2xl mb-2 block">🧬</span>
            <h3 className="font-serif text-sm text-accent-warm mb-1">Узнай свой архетип</h3>
            <p className="text-xs text-primary-400 leading-relaxed">
              Пройди короткую диагностику — и город определит, кто ты: Мудрец, Искатель, Бунтарь или один из 12 архетипов.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="p-4 rounded-xl bg-primary-800/20 border border-primary-700/30"
          >
            <span className="text-2xl mb-2 block">🌙</span>
            <h3 className="font-serif text-sm text-accent-warm mb-1">Практики каждый день</h3>
            <p className="text-xs text-primary-400 leading-relaxed">
              Медитация, дыхание, вечерняя рефлексия. Каждое действие снижает усталость и двигает город вперёд.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="p-4 rounded-xl bg-primary-800/20 border border-primary-700/30"
          >
            <span className="text-2xl mb-2 block">📜</span>
            <h3 className="font-serif text-sm text-accent-warm mb-1">Твоя личная летопись</h3>
            <p className="text-xs text-primary-400 leading-relaxed">
              Город запоминает каждый шаг и превращает твой путь в сказочную хронику.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="p-4 rounded-xl bg-primary-800/20 border border-primary-700/30"
          >
            <span className="text-2xl mb-2 block">👥</span>
            <h3 className="font-serif text-sm text-accent-warm mb-1">Гильдии и события</h3>
            <p className="text-xs text-primary-400 leading-relaxed">
              Присоединяйся к другим жителям, участвуй в событиях, выполняй квесты.
            </p>
          </motion.div>
        </div>

        {/* Real CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="relative z-20 space-y-4 pt-6 pb-4"
        >
          <Button
            onClick={onStart}
            fullWidth
            size="lg"
            className="text-lg"
          >
            Войти в город
          </Button>

          <p className="text-center text-xs text-primary-500 italic">
            Город хочет узнать тебя. Это займёт пару минут.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
